import { forwardRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Text, TextButton, Tooltip } from '@zillow/constellation-10';

import accessUtils from '../../../../srcLegacy/shared/utils/accessUtils';
import { useGetListingDetails } from '../../../hooks/queries/properties';

import { getListingSourcesRoute } from '../../../constants/urls';

import ListingIssuesModalTrigger from '../../modals/ListingIssuesModalTrigger';

import { useAuth } from '../../AuthProvider';
import { AccordionStateButton } from '../AccordionStateButton';

type Props = {
  title?: string;
  hideListingIssues?: boolean;
};

const ListingHeader = forwardRef<HTMLDivElement, Props>(({ title = '', hideListingIssues = false }: Props, ref) => {
  const { user } = useAuth();
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canFeedConnectSwitchSource = accessUtils.canFeedConnectSwitchSource({ user });
  const showListingSource =
    canFeedConnectSwitchSource &&
    !listingDetails?.isFeed &&
    listingDetails?.topDupesDetails?.sourceCount &&
    listingDetails?.topDupesDetails?.sourceCount > 1;
  const showListingIssues =
    canFeedConnectSwitchSource && listingDetails?.isFeed && !hideListingIssues && listingDetails?.active;

  if (!title && !showListingSource && !showListingIssues) {
    return null;
  }

  return (
    <>
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', md: 'row' },
          alignItems: { base: 'flex-start', md: 'center' },
        }}
        ref={ref}
        data-testid="ListingHeader"
      >
        <Box css={{ flex: '1', paddingBottom: '200' }}>
          <Text textStyle="heading-sm" asChild={true}>
            <p>{title}</p>
          </Text>
        </Box>
        {showListingSource && (
          <Box
            css={{
              marginRight: '400',
            }}
          >
            <TextButton asChild>
              <Link href={getListingSourcesRoute({ listingAlias })}>Manage listing source</Link>
            </TextButton>
            &nbsp;
            <Tooltip
              content="You have one or more other versions of this listing. Those listings come from other sources, such as feeds, and may be active on other sites."
              placement="top"
            />
          </Box>
        )}
        <AccordionStateButton isVisible={true} />
      </Box>
      {showListingIssues && <ListingIssuesModalTrigger showIcon={false} />}
    </>
  );
});

ListingHeader.displayName = 'ListingHeader';

export { ListingHeader };
export default ListingHeader;
